import React from 'react';
import { Mail, Phone } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';

const DELIVERY_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const OverviewTab = ({ clientDetail, editMode, editForm, setEditForm, savingChanges, onSave, onCancelEdit }) => (
  <div className="space-y-4 mt-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 uppercase">Account Number</p>
        <p className="font-medium">{clientDetail.account_number}</p>
      </div>
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 uppercase">Status</p>
        <Badge className={clientDetail.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
          {clientDetail.status}
        </Badge>
      </div>
    </div>

    {/* Pricing Tier */}
    <div className="p-3 bg-gray-50 rounded-lg">
      <p className="text-xs text-gray-500 uppercase mb-2">Pricing Tier</p>
      <div className="flex items-center gap-2">
        <Badge
          className={`${
            clientDetail.has_guaranteed_returns
              ? 'bg-purple-100 text-purple-800'
              : clientDetail.discount_percent > 15
                ? 'bg-blue-100 text-blue-800'
                : clientDetail.discount_percent > 0
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-800'
          }`}
          data-testid="client-detail-pricing-tier"
        >
          {clientDetail.client_type_name || 'Not Assigned'}
        </Badge>
        {clientDetail.discount_percent > 0 && (
          <span className="text-sm text-gray-600">{clientDetail.discount_percent}% discount</span>
        )}
        {clientDetail.has_guaranteed_returns && (
          <span className="text-xs text-purple-600 font-medium">Returns Eligible</span>
        )}
      </div>
    </div>

    {/* Contacts */}
    <div>
      <p className="text-sm font-medium text-gray-700 mb-2">Contacts</p>
      {clientDetail.contacts?.length > 0 ? (
        <div className="space-y-2">
          {clientDetail.contacts.map((contact, idx) => (
            <div key={contact.email || contact.name || idx} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <p className="font-medium">{contact.name}</p>
                {contact.is_primary && (
                  <Badge className="bg-amber-100 text-amber-700 text-xs">Primary</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500">{contact.role}</p>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {contact.email}
                </span>
                {contact.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {contact.phone}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">No contacts</p>
      )}
    </div>

    {/* Order Preferences */}
    <div>
      <p className="text-sm font-medium text-gray-700 mb-2">Order Preferences</p>
      {editMode ? (
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <Label>Minimum Order Amount ($)</Label>
            <Input
              type="number"
              value={editForm.minimum_order_amount}
              onChange={(e) => setEditForm(prev => ({ ...prev, minimum_order_amount: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div className="col-span-2">
            <Label>Allowed Delivery Days</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {DELIVERY_DAYS.map(day => (
                <label
                  key={day}
                  className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-amber-50 transition-colors"
                  style={{
                    borderColor: editForm.allowed_delivery_days?.includes(day) ? '#f59e0b' : '#e5e7eb',
                    backgroundColor: editForm.allowed_delivery_days?.includes(day) ? '#fffbeb' : 'white',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={editForm.allowed_delivery_days?.includes(day) || false}
                    onChange={(e) => {
                      const days = editForm.allowed_delivery_days || [];
                      setEditForm(prev => ({
                        ...prev,
                        allowed_delivery_days: e.target.checked ? [...days, day] : days.filter(d => d !== day),
                      }));
                    }}
                    className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-sm capitalize">{day}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Preferred Time Window</Label>
            <Select
              value={editForm.preferred_delivery_time}
              onValueChange={(v) => setEditForm(prev => ({ ...prev, preferred_delivery_time: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6:00 AM - 8:00 AM">6:00 AM - 8:00 AM</SelectItem>
                <SelectItem value="7:00 AM - 9:00 AM">7:00 AM - 9:00 AM</SelectItem>
                <SelectItem value="8:00 AM - 10:00 AM">8:00 AM - 10:00 AM</SelectItem>
                <SelectItem value="9:00 AM - 11:00 AM">9:00 AM - 11:00 AM</SelectItem>
                <SelectItem value="10:00 AM - 12:00 PM">10:00 AM - 12:00 PM</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="p-3 bg-gray-50 rounded-lg grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Minimum Order</p>
            <p className="font-medium">${clientDetail.preferences?.minimum_order_amount ?? 50}</p>
          </div>
          <div>
            <p className="text-gray-500">Preferred Time</p>
            <p className="font-medium">{clientDetail.preferences?.preferred_delivery_time || 'Any'}</p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-500 mb-1">Allowed Delivery Days</p>
            {clientDetail.preferences?.allowed_delivery_days?.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {clientDetail.preferences.allowed_delivery_days.map(day => (
                  <Badge key={day} className="bg-amber-100 text-amber-700 capitalize text-xs">{day}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-red-500 text-xs font-medium">No delivery days configured</p>
            )}
          </div>
        </div>
      )}
    </div>

    {editMode && (
      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button variant="outline" onClick={onCancelEdit}>Cancel</Button>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={onSave} disabled={savingChanges}>
          {savingChanges ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    )}
  </div>
);
